import { useState, useRef, useCallback, Fragment } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: customer pages use generated API outside src/
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ArrowLeft, Upload, FileText, AlertCircle, Check } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import type { TClaimStatus, TClaimLineItemStatus, IClaimApplicationData } from '@/@types/claims';
import { ClaimInfoCard } from '@/pages/insurance/claim-detail/ClaimInfoCard';
import {
  STATUS_BADGE,
  STATUS_BADGE_VARIANT,
  LINE_ITEM_STATUS_BADGE,
  LINE_ITEM_STATUS_BADGE_VARIANT,
} from '@/pages/insurance/claim-detail/claimDetailUtils';

const STATUS_STEPS: TClaimStatus[] = ['submitted', 'under_review', 'approved', 'paid'];

export function MyClaimDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const claimId = id as Id<'claims'>; // AS_JUSTIFIED: id from route params, narrow to typed Id
  const claim = useQuery(api.insurance.claims.get, { claimId });
  const generateUploadUrl = useMutation(api.storage.createUploadUrl);
  const uploadDocument = useMutation(api.insurance.claimDocuments.upload);

  const handleUpload = useCallback(
    async (file: File) => {
      setIsUploading(true);
      try {
        const url = await generateUploadUrl();
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': file.type },
          body: file,
        });
        if (!res.ok) throw new Error('Upload failed');
        const { storageId } = (await res.json()) as { storageId: Id<'_storage'> }; // AS_JUSTIFIED: fetch response JSON narrowed to known Convex storage upload shape
        await uploadDocument({
          claimId,
          storageId,
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
          documentType: 'other',
        });
        toast.success('Document uploaded successfully');
      } catch {
        toast.error('Failed to upload document');
      } finally {
        setIsUploading(false);
      }
    },
    [claimId, generateUploadUrl, uploadDocument]
  );

  if (claim === undefined) {
    return (
      <div className="flex-1 p-8 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (claim === null) {
    return (
      <div className="flex-1 p-8">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <div className="mt-8 text-center text-muted-foreground">Claim not found.</div>
      </div>
    );
  }

  const status = claim.status as TClaimStatus; // AS_JUSTIFIED: Convex return status string narrowed to TClaimStatus for STATUS_STEPS lookup
  const currentStepIndex = STATUS_STEPS.indexOf(status);
  const needsInfo = status === 'additional_info_required';

  return (
    <div className="flex-1 p-8">
      <Button variant="ghost" className="mb-4" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>

      <div className="flex items-center gap-3 mb-6">
        <h1 className="text-2xl font-semibold font-mono">{claim.claimNumber}</h1>
        <Badge
          variant={STATUS_BADGE_VARIANT[status]}
          className={cn('text-xs font-medium capitalize', STATUS_BADGE[status])}
        >
          {status.replace(/_/g, ' ')}
        </Badge>
      </div>

      {needsInfo && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-claim-info-required-border bg-claim-info-required-bg p-4">
          <AlertCircle className="h-5 w-5 text-claim-info-required mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-claim-info-required">Additional information required</p>
            <p className="text-sm text-claim-info-required/80 mt-1">
              Please upload additional documents to continue processing your claim.
            </p>
          </div>
          <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={isUploading}>
            <Upload className="h-4 w-4 mr-2" />
            {isUploading ? 'Uploading…' : 'Upload Document'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            onChange={e => {
              const f = e.target.files?.[0];
              if (f) void handleUpload(f);
            }}
          />
        </div>
      )}

      {/* Status Timeline */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Claim Progress</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            {STATUS_STEPS.map((step, i) => (
              <div key={step} className="flex items-center gap-2 flex-1 last:flex-none">
                <div
                  className={cn(
                    'h-7 w-7 rounded-full flex items-center justify-center shrink-0 text-xs font-medium',
                    i < currentStepIndex
                      ? 'bg-primary text-primary-foreground'
                      : i === currentStepIndex
                        ? 'bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2'
                        : 'bg-muted text-muted-foreground'
                  )}
                >
                  {i < currentStepIndex ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </div>
                <span
                  className={cn(
                    'text-xs capitalize hidden sm:block',
                    i <= currentStepIndex ? 'text-foreground' : 'text-muted-foreground'
                  )}
                >
                  {step.replace(/_/g, ' ')}
                </span>
                {i < STATUS_STEPS.length - 1 && (
                  <div
                    className={cn('h-px flex-1', i < currentStepIndex ? 'bg-primary' : 'bg-border')}
                  />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Claim Details — accordion sections matching wizard steps */}
      <div className="mb-6">
        <ClaimInfoCard
          claim={claim}
          claimApplicationData={claim.claimApplicationData as IClaimApplicationData | undefined} // AS_JUSTIFIED: claimApplicationData shape matches IClaimApplicationData from wizard
        />
        {claim.rejectionReason && (
          <Card className="mt-3">
            <CardContent className="pt-4">
              <p className="text-sm text-claim-rejected font-medium">Rejection Reason</p>
              <p className="text-sm text-claim-rejected mt-1">{claim.rejectionReason}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Line Items */}
      {claim.lineItems.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Line Items</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead>Claimed</TableHead>
                  <TableHead>Approved</TableHead>
                  <TableHead>Paid</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {claim.lineItems.map(li => (
                  <Fragment key={li._id}>
                    <TableRow>
                      <TableCell>{li.description}</TableCell>
                      <TableCell>
                        {li.currency} {li.amountClaimed.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {li.amountApproved != null
                          ? `${li.currency} ${li.amountApproved.toLocaleString()}`
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {li.amountPaid != null
                          ? `${li.currency} ${li.amountPaid.toLocaleString()}`
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            LINE_ITEM_STATUS_BADGE_VARIANT[li.status as TClaimLineItemStatus]
                          } // AS_JUSTIFIED: line item status narrowed to TClaimLineItemStatus
                          className={cn(
                            'text-xs capitalize',
                            LINE_ITEM_STATUS_BADGE[li.status as TClaimLineItemStatus]
                          )} // AS_JUSTIFIED: same narrowing
                        >
                          {li.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                    {li.rejectionReason && (
                      <TableRow className="hover:bg-transparent border-0">
                        <TableCell colSpan={5} className="pt-0 pb-3 pl-8">
                          <div className="rounded-md border border-claim-rejected-border bg-claim-rejected-bg px-4 py-3">
                            <p className="text-xs font-medium text-claim-rejected mb-1">
                              Rejection Reason
                            </p>
                            <p className="text-sm text-claim-rejected">{li.rejectionReason}</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Documents */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Documents</CardTitle>
          {!needsInfo && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                <Upload className="h-4 w-4 mr-2" />
                {isUploading ? 'Uploading…' : 'Upload'}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={e => {
                  const f = e.target.files?.[0];
                  if (f) void handleUpload(f);
                }}
              />
            </>
          )}
        </CardHeader>
        <CardContent>
          {claim.documents.length === 0 ? (
            <p className="text-sm text-muted-foreground">No documents uploaded yet.</p>
          ) : (
            <ul className="space-y-2">
              {claim.documents.map(doc => (
                <li key={doc._id} className="flex items-center gap-3 text-sm">
                  <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="flex-1 truncate">{doc.fileName}</span>
                  <span className="text-muted-foreground capitalize">{doc.documentType}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Separator className="my-6" />
    </div>
  );
}
